import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import type { ReviewCardAdminRow } from '../../../sdk/@types/Revisao';
import { cardTipoMostrado } from './cardTipo';

/**
 * Rótulos de uma linha da lista administrativa. A tabela e a ficha estreita
 * leem daqui, para não formatar a taxa, a data ou a contagem de marcações
 * cada uma do seu jeito.
 *
 * O tipo da linha não se decide aqui por um `layout === …` solto: a contagem
 * de marcações só existe quando `cardTipoMostrado` devolve Oclusão.
 */

/** `#` mais o inteiro com pelo menos 4 dígitos (`142` → `#0142`). Não corta números maiores. */
export function formatShortId(shortId: number): string {
  return `#${String(Math.trunc(shortId)).padStart(4, '0')}`;
}

/** Ausente quando a leitura ainda não mandou `number`. A tela não inventa um. */
export function shortIdLabel(shortId: number | undefined): string | null {
  if (typeof shortId !== 'number' || !Number.isFinite(shortId)) return null;
  return formatShortId(shortId);
}

/**
 * Taxa de acerto entre os alunos.
 *
 * A leitura manda `successRate: null` — e o serviço deixa o número fora de
 * `accuracy.rate` — quando não há respostas suficientes. Ausência é
 * "Indisponível", nunca "0%": ninguém ter respondido não é todo mundo ter
 * errado. Um número, inclusive 0, é a taxa que a leitura mandou (amostra
 * suficiente e todo mundo errou é zero por cento de verdade). O limiar do
 * que é "suficiente" fica no servidor; esta função não inventa outro.
 */
export function accuracyLabel(card: Pick<ReviewCardAdminRow, 'accuracy'>): string {
  const rate = card.accuracy?.rate;
  if (typeof rate !== 'number' || !Number.isFinite(rate)) return 'Indisponível';
  const formatted = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 }).format(rate);
  return `${formatted}%`;
}

/** `updatedAt` ausente ou ilegível não derruba a linha — a célula fica "—". */
export function updatedLabel(updatedAt: string | undefined): string {
  if (!updatedAt) return '—';
  const date = parseISO(updatedAt);
  if (Number.isNaN(date.getTime())) return '—';
  return format(date, 'dd/MM/yyyy', { locale: ptBR });
}

/**
 * Quantas marcações a linha de oclusão anuncia. `null` nos outros tipos —
 * a frase só aparece quando o tipo mostrado é Oclusão.
 *
 * O array `masks` do cartão modelo é a contagem. `maskTotal` só entra quando
 * a leitura não trouxe o array (a unidade de estudo manda o total à parte).
 */
export function markingCount(
  card: Pick<ReviewCardAdminRow, 'layout' | 'masks' | 'maskTotal'>,
): number | null {
  if (cardTipoMostrado(card) !== 'Oclusão') return null;
  if (Array.isArray(card.masks)) return card.masks.length;
  if (typeof card.maskTotal === 'number' && Number.isFinite(card.maskTotal)) {
    return Math.max(0, Math.trunc(card.maskTotal));
  }
  return 0;
}

export function markingLabel(count: number): string {
  const n = Math.max(0, Math.trunc(count));
  return n === 1 ? '1 marcação' : `${n} marcações`;
}
